import { useQuery } from '@tanstack/react-query'
import type { Board, Overview, ProjectList, WorkspaceSummary } from '@one-deployment/api'
import type { Stage } from '@one-deployment/shared'
import { enc, get } from '@/lib/api'
import { useGlobalQueryOpts, useLiveQueryOpts } from '@/lib/live'
import { qk } from '@/lib/query'

/**
 * The one poll every signed-in screen shares: sidebar, home and banners read
 * the same answer. The global topic invalidates it; the minute poll is the fallback.
 */
export function useOverview() {
  const live = useGlobalQueryOpts(60_000)
  return useQuery({ queryKey: qk.overview(), queryFn: ({ signal }) => get<Overview>('/api/overview', signal), ...live })
}

/** Every project the reader can see, with `paused` and their role on it. */
export function useProjects() {
  return useQuery({ queryKey: qk.projects(), queryFn: ({ signal }) => get<ProjectList>('/api/projects', signal), refetchInterval: 60_000 })
}

/** One project's workspaces and stage counts; kept current by its project topic when one is open. */
export function useBoard(project: string, enabled = true) {
  const live = useLiveQueryOpts(`project:${project}`)
  return useQuery({ queryKey: qk.board(project), queryFn: ({ signal }) => get<Board>(`/api/projects/${enc(project)}/board`, signal), enabled, ...live })
}

/** Stages a workspace does not come back from: the archive, not the sidebar. */
export const FINISHED: ReadonlySet<Stage> = new Set<Stage>(['merged', 'dropped', 'done_elsewhere', 'declined', 'verified', 'rejected'])

export const isActive = (w: WorkspaceSummary) => !FINISHED.has(w.stage)
/** A Claude session is printing for it right now. */
export const isPrinting = (w: WorkspaceSummary) => w.liveJob?.status === 'leased'
